/**
 * Possible Root Cause bullets can carry a short plain-language explainer for a
 * clinical term, authored inline in the sheet (and stored verbatim in the DB) as
 *
 *   Addison’s disease [Hover text: Primary adrenal insufficiency that causes
 *   low cortisol and often low aldosterone.]
 *
 * The token is an annotation, never body copy — no surface should ever print
 * the literal "[Hover text: …]". Each surface picks its treatment:
 *
 *   - the on-screen tab       → tooltip (further-considerations-section.tsx)
 *   - the client PDF          → printed after the bullet in smaller italics,
 *                               because paper has no hover
 *   - plain-text surfaces     → folded into the sentence as a parenthetical
 *     (the Notes digest, the AI raw-data export)
 *
 * Authors write the token mid-bullet as often as at the end (e.g. "Cushing’s
 * syndrome [Hover text: …] (2)"), so removing it has to close the gap it leaves
 * behind rather than assume it was trailing.
 */

// Case-insensitive on the label so an author typing "[Hover Text: …]" still
// parses rather than shipping the raw token to a client report. Leading
// whitespace is part of the match so removing a mid-sentence token doesn't
// leave a double space before what follows.
const HOVER_TOKEN = /\s*\[hover\s*text:\s*([^\]]*)\]/gi;

/** True when an item carries at least one hover annotation. */
export function hasHoverText(item: string): boolean {
  if (!item) return false;
  HOVER_TOKEN.lastIndex = 0;
  return HOVER_TOKEN.test(item);
}

/**
 * Split a bullet into the text the report shows and its explainer.
 *
 * A bullet with more than one token (none today, but the format allows it)
 * collapses to a single explainer joined with spaces — one bullet gets one
 * tooltip, so two annotations on one line read as one paragraph.
 */
export function parseHoverText(item: string): { text: string; hover?: string } {
  if (!item) return { text: '' };
  const hovers: string[] = [];
  HOVER_TOKEN.lastIndex = 0;
  const text = item
    .replace(HOVER_TOKEN, (_match, body: string) => {
      const trimmed = (body || '').trim();
      if (trimmed) hovers.push(trimmed);
      return '';
    })
    // A token removed from between two words leaves the following space behind;
    // one removed before punctuation can strip the space the sentence needed.
    .replace(/\s{2,}/g, ' ')
    .trim();
  return hovers.length ? { text, hover: hovers.join(' ') } : { text };
}

/** The bullet with its annotation removed. */
export function stripHoverText(item: string): string {
  return parseHoverText(item).text;
}

/**
 * The bullet with its annotation folded in as a parenthetical, for surfaces
 * that are plain text and cannot hover — the Notes digest a practitioner pastes
 * into their own note, and the AI raw-data export. Keeps the explanation rather
 * than dropping it, and never leaks the token syntax.
 */
export function inlineHoverText(item: string): string {
  const { text, hover } = parseHoverText(item);
  if (!hover) return text;
  // The explainer is authored as a full sentence, so it reads correctly as a
  // parenthetical only once its terminal period is dropped.
  return `${text} (${hover.replace(/\.$/, '')})`;
}
